// RTableGroup delta: the group leads a three-channel delta between two of its
// positions, mirroring RSet's full/bounded split but over MORE than one
// observed object (its RSchema plus every bound foreign group).
//
// Three channels:
//   - schema sub-delta (uniform): `schemaChanges` carries column defaults,
//     table/column drops, FK/restriction/concurrentDeletes flips as RSchema
//     changes. The consumer applies these to rows NOT present in the row-walk;
//     they are never enumerated per row (an old untouched row affected only by
//     a default/drop produces no RowChange).
//   - per-table row-walk (positional): each touched member table's RTableChanges
//     in the root delta's `nested` map, keyed by the table id. A row appears
//     only when its liveness flipped or a written value moved (see ../rtable/delta.ts).
//   - op verdict flips: `opVerdictChanges` lists group DAG entries whose
//     at-use void verdict changed between the start and end view horizons
//     (insert/update/delete/bundle ops and gated observes that flip). Row
//     channel materializes effects; op channel explains reconciliation flips.
//
// Strategies (RObject.setDeltaStrategy switch on the group):
//   - full: bound = empty, walk all history from genesis. The reference
//     implementation (slow, obviously correct), used for parity.
//   - bounded: bound = GLB(fork meet, projected bound of the schema, projected
//     bound of each bound foreign group) via combineObserverRevisionBounds. The
//     at-use semantics make this floor exact: below it no observed
//     object can revise an old row's verdict, so the walk above it captures
//     every positional change.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { json } from "@hyper-hyper-space/hhs3_json";
import { version, computeForkMeet, combineObserverRevisionBounds, walkEntriesBackwardsToBound, } from "@hyper-hyper-space/hhs3_mvt";
import { computeOpVerdictFlips } from "./op_delta.js";
export { formatOpVoidDetail } from "./op_delta.js";
export class RTableGroupDelta {
    constructor(start, end, revisionBound, root) {
        this.start = start;
        this.end = end;
        this.revisionBound = revisionBound;
        this.type = root.type;
        this.changes = root.changes;
        this.nested = root.nested;
    }
    get schemaChanges() { return this.changes.schemaChanges; }
    get opVerdictChanges() { return this.changes.opVerdictChanges; }
    // Per-table row changes, keyed by table id (the nested delta subtree).
    get tableChanges() {
        const map = new Map();
        for (const [id, child] of this.nested)
            map.set(id, child.changes);
        return map;
    }
}
export class RTableGroupDeltaAccumulator {
    constructor(group, start, end) {
        this.group = group;
        this.start = start;
        this.end = end;
        // one child row-accumulator per touched member table
        this.tableAccumulators = new Map();
    }
    // Route the entry to each member table it touches (the `tables` meta lists
    // them; ref-advance entries carry none and route nowhere). The schema
    // sub-delta is computed wholesale in finalize, so deploy / observe entries
    // need no per-entry routing here.
    ingest(entry) {
        return __awaiter(this, void 0, void 0, function* () {
            const tablesMeta = entry.meta['tables'];
            if (tablesMeta === undefined)
                return false;
            let changed = false;
            for (const table of json.fromSet(tablesMeta)) {
                let child = this.tableAccumulators.get(table);
                if (child === undefined) {
                    child = this.group.makeTable(table).createDeltaAccumulator(this.start, this.end);
                    this.tableAccumulators.set(table, child);
                }
                if (yield child.ingest(entry))
                    changed = true;
            }
            return changed;
        });
    }
    finalize() {
        return __awaiter(this, void 0, void 0, function* () {
            // schema sub-delta channel: the schema's own delta between the versions
            // observed at start and end (identical under either group strategy —
            // RSchema is confluent / bounded-exact).
            const schema = yield this.group.getSchemaObject();
            const vStart = yield this.group.resolveSchemaVersion(this.start, this.start);
            const vEnd = yield this.group.resolveSchemaVersion(this.end, this.end);
            const schemaDelta = yield schema.computeDelta(vStart, vEnd);
            const schemaChanges = schemaDelta.changes;
            // per-table row channel: drop tables with no actual row changes
            const nested = new Map();
            for (const [table, child] of this.tableAccumulators) {
                const childChanges = yield child.finalize();
                if (childChanges.changes.rowChanges.length > 0) {
                    nested.set(this.group.makeTable(table).getId(), childChanges);
                }
            }
            return {
                type: this.group.getType(),
                changes: { schemaChanges, opVerdictChanges: [] },
                nested,
            };
        });
    }
}
function emptyChanges(type) {
    return {
        type,
        changes: { schemaChanges: { tableChanges: [] }, opVerdictChanges: [] },
        nested: new Map(),
    };
}
export function computeRTableGroupDelta(group, rawDag, strategy, start, end) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const fork = yield rawDag.findForkPosition(start, end);
        if (fork.forkA.size > 0) {
            throw new Error("computeDelta requires END to extend START");
        }
        // end == start (no new entries): nothing differs, schema included.
        if (fork.forkB.size === 0) {
            return new RTableGroupDelta(start, end, fork.commonFrontier, emptyChanges(group.getType()));
        }
        let revisionBound;
        if (strategy === 'full') {
            revisionBound = version(); // empty bound: walk the whole history of `end`
        }
        else if (strategy === 'bounded') {
            const meet = yield computeForkMeet(rawDag, fork.common);
            const observed = yield group.getObservedObjects();
            // referenced objects participate in bound projection via their own
            // bounded delta (mirrors RSet -> RCap); the schema has no strategy switch.
            for (const obj of observed) {
                (_b = (_a = obj).setDeltaStrategy) === null || _b === void 0 ? void 0 : _b.call(_a, 'bounded');
            }
            revisionBound = yield combineObserverRevisionBounds(group, meet, end, observed);
        }
        else {
            throw new Error("Invalid delta strategy: " + strategy);
        }
        const entries = yield walkEntriesBackwardsToBound(rawDag, end, revisionBound);
        const acc = new RTableGroupDeltaAccumulator(group, start, end);
        for (const entry of entries) {
            yield acc.ingest(entry);
        }
        const root = yield acc.finalize();
        root.changes.opVerdictChanges = yield computeOpVerdictFlips(group, entries, start, end);
        return new RTableGroupDelta(start, end, revisionBound, root);
    });
}
